import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Tabs, Tab, Modal, Button, Typography, Avatar, IconButton } from '@mui/material';

import { RequestNextApi } from '../../api/Request';
import { ProgressContext } from '../../components/Context/progressContext';
import MarketingNavigation from '../../navigation/marketing';
import { ShowNotification } from '../../utils/notification/snackbarUtils';
import PageFooter from '../../components/ArticleBlocks/PageFooter';
import { CloseOutlined } from '@mui/icons-material';

export default function Partners() {
    const [partners, setPartners] = useState({});
    const [activeLevel, setActiveLevel] = useState('1');
    const [activePartner, setActivePartner] = useState(false);
    const { update: updateProgress } = useContext(ProgressContext);

    useEffect(() => {
        const fetchPage = async () => {
            try {
                const res = await RequestNextApi.post('/partnerhub/partnerhub-fetch-profiles');

                if (res.data.success) {
                    const results = res.data.results;
                    const data = {};

                    for (let i = 0; i < results.length; i++) {
                        const partner = results[i];
                        data[partner.partner_level] = data[partner.partner_level] || {
                            partner_type: partner.partner_type,
                            items: [],
                        };
                        data[partner.partner_level].items.push(partner);
                    }

                    setPartners(data);
                } else {
                    ShowNotification.error('Fetch page failed');
                }
            } catch (error) {
                ShowNotification.error('Fetch page failed');
            }
        };

        const initPage = async () => {
            updateProgress({ isLoading: true });
            await fetchPage();
            updateProgress({ isLoading: false });
        };

        initPage();
    }, []);

    const renderPartners = useCallback(() => {
        if (partners && partners[activeLevel]) {
            return (
                <div className='py-4 px-8 flex flex-col sm:grid 3xl:grid-cols-5 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 xs:grid-cols-1 gap-6 auto-rows-[1fr]'>
                    {partners[activeLevel].items.map(
                        (item, index) =>
                            item.firmname !== 'Kingdom Advisors' && (
                                <div key={index} className='bg-white border rounded-xl border-solid border-primary-100 shadow flex flex-col justify-between p-4 text-center cursor-pointer' onClick={() => setActivePartner(index)}>
                                    <div className='flex-1'>
                                        <img src={item.logo} className='max-w-full h-full object-contain' />
                                    </div>
                                    <Typography variant='body1' className='mt-2'>
                                        {item.firmname}
                                    </Typography>
                                    <Typography variant='body2' color='secondary' className='mt-1.5'>
                                        {item.partner_type}
                                    </Typography>
                                    <Typography variant='body2' className='mt-1.5 block sm:hidden'>
                                        {item.org_descrip}
                                    </Typography>
                                </div>
                            )
                    )}
                </div>
            );
        }
    }, [partners, activeLevel, setActivePartner]);

    const renderPartnerModal = useCallback(() => {
        if (activePartner !== false) {
            const item = partners[activeLevel].items[activePartner];
            return (
                <Modal onClose={() => setActivePartner(false)} open>
                    <div className='border border-solid border-primary-100 shadow relative max-w-full w-[700px] bg-white mx-auto p-5 rounded-xl top-1/2 -translate-y-1/2 max-h-screen overflow-scroll space-y-4'>
                        <div className='flex gap-4 items-center relative'>
                            <Avatar
                                alt={item.firmname}
                                src={item.logo}
                                className='w-20 h-20 border border-solid border-primary-200 rounded-full'
                                classes={{
                                    img: 'object-contain',
                                }}
                            />
                            <div>
                                <Typography variant='h3' className='text-primary-900 mb-2'>
                                    {item.firmname}
                                </Typography>
                                <Typography variant='caption' color='secondary' className='px-2.5 py-2 rounded-lg border bg-primary-50'>
                                    {item.partner_type}
                                </Typography>
                            </div>

                            <div className='absolute top-0 right-0'>
                                <IconButton color='primary' onClick={() => setActivePartner(false)}>
                                    <CloseOutlined />
                                </IconButton>
                            </div>
                        </div>
                        {item.org_descrip && <Typography>{item.org_descrip}</Typography>}
                        <a href={item.firmweb} target='_blank' className='block'>
                            <Typography variant='body1' color='secondary' className='font-bold'>
                                Click here to visit {item.firmname}
                            </Typography>
                        </a>
                    </div>
                </Modal>
            );
        }
    }, [partners, activeLevel, activePartner, setActivePartner]);

    return (
        <MarketingNavigation>
            <div className='marketing-partners'>
                <div className='bg-primary-500 py-10 px-10 lg:px-20 text-white text-center'>
                    <Typography component="h1" className='text-4xl my-5'>Partners</Typography>
                    <Typography component="p" className='text-xl my-5'>
                        We are delighted to collaborate with outstanding product and service providers and
                        difference-making ministries that offer values-aligned solutions for advisors and their clients.
                    </Typography>
                    <Typography component="p" className='text-xl my-5'>
                        Our relationship with these providers is a nonexclusive, arm’s-length sponsor relationship.
                        Being a partner with Kingdom Advisors does not imply endorsement of any religious views or
                        beliefs. We honor the responsibility of financial professionals in our community to discern
                        whether a particular product or service provider aligns with the needs of their clients.
                    </Typography>
                    <Typography component="p" className='text-xl'>
                        For more information about our industry-wide relationships, see&nbsp;
                        <a target='_blank' className='text-white underline' href='https://kawebresources.s3.amazonaws.com/sitedownloads/Our%20Professional%20Commitments.pdf'>
                            Our Professional Commitments
                        </a>
                        .
                    </Typography>
                </div>

                <div className='bg-gray-100 py-10 px-10 lg:px-20 text-black text-center'>
                    <Typography component="p" className='text-base'>
                        Kingdom Advisors offers a range of sponsorship levels that provide benefits at our annual conference and throughout the year. To learn more, read our&nbsp;
                        <a target='_blank' className='text-black underline' href='https://kawebresources.s3.amazonaws.com/resourcelibraryfiles/2025_Partner_Program_FAQ.pdf'>
                            Partner Program FAQ
                        </a>
                        , download our current&nbsp;
                        <a target='_blank' className='text-black underline' href='https://kawebresources.s3.amazonaws.com/resourcelibraryfiles/2025-2026-Sponsor-Benefits.pdf'>
                            Partner Benefits
                        </a>
                        , or reach out to&nbsp;
                        <a target='_blank' className='text-black underline' href='mailto:stuart.easterly@kingdomadvisors.com'>
                            stuart.easterly@kingdomadvisors.com
                        </a>
                        .
                    </Typography>
                </div>

                <div className="py-5 px-4 sm:px-8 bg-white">
                    <div className='flex gap-2 sm:gap-4'>
                        {Object.keys(partners).map((index) => (
                          <Button variant='outlined' color={index === activeLevel ? 'secondary' : 'white'} key={index} onClick={() => setActiveLevel(index)} classes={{
                              root: 'text-xs sm:text-sm'
                          }}>
                              {partners[index].partner_type}
                          </Button>
                        ))}
                    </div>
                </div>
                <div className='bg-white'>{renderPartners()}</div>
                {renderPartnerModal()}
            </div>
            <PageFooter/>
        </MarketingNavigation>
    );
}

Partners.getLayout = (page) => <>{page}</>;
Partners.isPublic = true;
